/**
 * userActions.js
 * Talkroute
 *
 * Created by Vel Genov on 12/8/18
 * Copyright (c) 2018 Talkroute. All right reserved.
 */
import { push } from "connected-react-router";

import routes from "@constants/routes";
import userConstants from "@constants/userConstants";

import apiServiceInstance from "@services/TalkrouteAPIService";

import FilePathHelper from "@helpers/filePathHelper";

import { appConfig } from "../index";

import alertActions from "./alertActions";
import { clearErrors } from "../actions/errorActions";
import AuthService from "@services/Auth.service";
import { ThunkDispatch } from "redux-thunk";
import DeviceService from "@services/Devices.service";
import { RootState } from "@helpers/store/store";
import { IFeature } from "@models/Account.models";
import { RefreshTokenStatus } from "@reducers/authenticationReducer";
import UserService from "@services/User.service";


/**
 * A login action
 *
 * Send a request to Talkroute API to authenticate a user
 *
 * @param {String} userName
 * @param {String} password
 */
export const login = (userName, password) => (dispatch) => {
  dispatch(request({ userName }));

  return apiServiceInstance
    .authenticate(userName, password)
    .then((response: any) => {
      const {
        accessToken,
        refreshToken,
        tokenExpiresAt,
        webrtcUserName,
        webrtcPassword,
        firstName,
        lastName,
        companyName,
        userId,
        accountId
      } = response;
      dispatch(
        success(
          userName,
          accessToken,
          refreshToken,
          tokenExpiresAt,
          webrtcUserName,
          webrtcPassword,
          firstName,
          lastName,
          companyName,
          userId,
          accountId
        )
      );
      dispatch(setRefreshTokenStatus(RefreshTokenStatus.VALID_TOKEN_AVAILABLE))
      dispatch(alertActions.clear());
      dispatch(clearErrors());
      dispatch(push(routes.HOME));
      return accessToken;
    })
    .catch((error) => {
      dispatch(failure(error));
    });

  // called when a login attempt request is sent
  function request(user) {
    return { type: userConstants.LOGIN_REQUEST, payload: { user } };
  }
  // called when the login attempt was successfull
  function success(
    user: string,
    accessToken: string,
    refreshToken: string,
    tokenExpiresAt: number,
    webrtcUserName: string,
    webrtcPassword: string,
    firstName: string,
    lastName: string,
    companyName: string,
    userId: string,
    accountId: number,
  ) {
    return {
      type: userConstants.LOGIN_SUCCESS,
      payload: {
        user,
        accessToken,
        refreshToken,
        tokenExpiresAt,
        webrtcUserName,
        webrtcPassword,
        firstName,
        lastName,
        companyName,
        userId,
        accountId
      },
    };
  }

  // caleed when the login attempt fails
  function failure(error) {
    return {
      type: userConstants.LOGIN_FAILURE,
      payload: {
        error,
      },
    };
  }
};

// called when login is successfull and there's impersonation
export const impersonateSuccess = (impersonateUserId: number, userEmail: string, userFirstName: string, userLastName: string, accountId: number, companyName: string) => {
  return {
    type: userConstants.IMPERSONATE_LOGIN_SUCCESS,
    payload: { impersonateUserId, userEmail, userFirstName, userLastName, accountId, companyName }
  };
}

export const clearAuthError = () => ({ type: userConstants.CLEAR_AUTH_ERROR });

/**
 * Logout action
 *
 * Send a request to Talkroute API to log a user out
 *
 */
export const logout = () => (dispatch, getState: () => RootState) => {
  dispatch({ type: userConstants.LOGOUT });
  dispatch(setUserTokenNotReady());
  dispatch(push(routes.LOGIN));
  performPostLogoutCleanup();
  appConfig.axios.interceptors.response.eject(apiServiceInstance.getInterceptor())
  return apiServiceInstance
    .logout()
    .then((token) => {
      return token
    })
    .catch((error) => error);
};

/**
 * Get the permissions for the current user
 */
export const getUserPermissions = () => {
  return (dispatch: ThunkDispatch<any, any, any>, getState: () => RootState) => {
    const pending = getState().authentication.userPermissionsPending;

    // if (!pending) {
      dispatch(getUserPermissionsPending(true));
      apiServiceInstance
      .getUserPermissions()
      .then((data) => {
        dispatch(getUserPermissionsSuccess(data));
        dispatch(getUserPermissionsPending(false));
        return data;
      })
      .catch((err) => {
        dispatch(getUserPermissionsPending(false));
        return err;
      });
    // }
  }
};

/**
 * Get the preferences for the current user
 */
export const getUserPreferences = () => {
  return async (dispatch, getState: () => RootState) => {
    const { GetUserPreferences } = UserService();
    const userId = getState().authentication.userId;
    try {
      const data = await GetUserPreferences(String(userId));
      if (data) {
        dispatch(setUserCallHistoryLastSeen(data.callHistoryLastVisited));
      }
    } catch (error) {
      console.warn(error);
    }
  }
};

/**
 * Update the preferences for the current user
 */
export const updateUserPreferences = (data: any) => {
  return async (dispatch, getState: () => RootState) => {
    const { UpdateUserPreferences, GetUserPreferences } = UserService();
    const userId = getState().authentication.userId;
    try {
        const res = await UpdateUserPreferences(String(userId), data);
        const newRes = await GetUserPreferences(String(userId));
        dispatch(setUserCallHistoryLastSeen(newRes.callHistoryLastVisited));
    } catch (error) {
      console.warn(error);
    }
  }
}

export const setRefreshAccessTokenPending = payload => ({
  type: userConstants.REFRESH_TOKEN_PENDING,
  payload
})

/**
 * Block unwanted user requests when pending
 */
 const getUserPermissionsPending = (payload: boolean) => {
  return {
    type: userConstants.GET_USER_PERMISSIONS_PENDING,
    payload,
  };
 }

/**
 * Successfully get user permissions
 *
 * @param data The object with userRole, userPermissions
 */
export const getUserPermissionsSuccess = (data) => ({
  type: userConstants.GET_USER_PERMISSIONS,
  payload: { data },
});

/**
 * Called to clean up user data after logout
 */
const performPostLogoutCleanup = () => {
  const filePathHelper = new FilePathHelper();
  // Remove voicemail and text message attachment data
  const voicemailFilePath = filePathHelper.getVoicemailFilePath();
  const messageAttachmentFilePath = filePathHelper.getMessageAttachmentBaseFilePath();

  appConfig.fileStorage?.exists(voicemailFilePath) && appConfig.fileStorage?.remove(voicemailFilePath);
  appConfig.fileStorage?.exists(messageAttachmentFilePath) &&  appConfig.fileStorage?.remove(messageAttachmentFilePath);
  filePathHelper.clearTempFoler();
};

export const setPermittedFeatures = (payload: IFeature[]) => ({
  type: userConstants.SET_PERMITTED_FEATURES,
  payload
})

export const setRefreshTokenStatus = (payload: RefreshTokenStatus) => ({
  type: userConstants.UPDATE_REFRESH_TOKEN_STATUS,
  payload,
})

/**
 * Successfully refreshed token
 *
 * @param data The object with new token info
 */
export const refreshAccessTokenSuccess = (data) => ({
  type: userConstants.TOKEN_REFRESHED,
  payload: data,
});


export const setUserTokenReady = () => ({
  type: userConstants.USER_TOKEN_READY,
});

export const setUserTokenNotReady = () => ({
  type: userConstants.USER_TOKEN_NOT_READY    
})

export const setAccessTokenValidity = (payload: boolean) => ({
  type: userConstants.SET_ACCESS_TOKEN_IS_VALID,
  payload
})

export const setUserRole = (payload: string) => ({
  type: userConstants.SET_USER_ROLE,
  payload
})

export const setUserCallHistoryLastSeen = (payload: number) => ({
  type: userConstants.SET_CALL_HISTORY_LAST_VIEWED,
  payload
})

const setMoxtraAccessToken = (moxtraAccessToken: string, moxtraAccessTokenExpDate: number) => ({
  type: userConstants.SET_MOXTRA_ACCESS_TOKEN,
  payload: {moxtraAccessToken, moxtraAccessTokenExpDate}
})

const userActions = {
  login,
  clearAuthError,
  logout,
  getUserPermissions,
  getUserPermissionsSuccess,
  setMoxtraAccessToken
};

export default userActions;
